import { useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ForgotPasswordForm } from '@/components/ForgotPasswordForm';
import { Logo } from '@/components/Logo';
import { useTheme } from '@/components/ThemeProvider';
import Translator, { useTranslation } from 'components/i18n/Translator';


import { ChainlitContext, useAuth } from 'client-types/*';

export const ForgotPasswordError = new Error(
  'Error sending reset email. Please try again later.'
);

export default function ForgotPassword() {
  const { t } = useTranslation();
  const { data: config, user } = useAuth();
  const apiClient = useContext(ChainlitContext);
  const navigate = useNavigate();
  const { variant } = useTheme();
  const isDarkMode = variant === 'dark';

  const handleForgotPassword = async (email: string) => {
    try {
      const response = await apiClient.forgotPassword(email);
      return response;
    } catch (error: any) {
      throw new Error(error.detail || error.message || 'Failed to send reset email');
    }
  };

  const handleVerifyCode = async (token: string, code: string) => {
    try {
      const response = await apiClient.verifyCode(token, code);
      return response;
    } catch (error: any) {
      throw new Error(error.detail || error.message || 'Failed to verify code');
    }
  };

  const handleResetPassword = async (token: string, code: string, newPassword: string) => {
    try {
      const response = await apiClient.resetPassword(token, code, newPassword);
      return response;
    } catch (error: any) {
      throw new Error(error.detail || error.message || 'Failed to reset password');
    }
  };

  const handleSuccess = () => {
    // Success handled in component - navigate to login after success
    setTimeout(() => {
      navigate('/login');
    }, 2000);
  };

  useEffect(() => {
    // Only redirect if config is loaded and user is authenticated
    if (config && user) {
      navigate('/');
    }
  }, [config, user]);

  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      <div className="flex flex-col gap-4 p-6 md:p-10">
        <div className="flex justify-center gap-2 md:justify-start">
          <Logo className="w-[150px]" />
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-xs">
            <ForgotPasswordForm
              onForgotPassword={handleForgotPassword}
              onVerifyCode={handleVerifyCode}
              onResetPassword={handleResetPassword}
              onSuccess={handleSuccess}
            />
            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                <Translator path="auth.forgotPassword.actions.backToLogin" />{' '}
                <Link
                  to="/login"
                  className="text-primary hover:underline font-medium"
                >
                  <Translator path="auth.login.alternativeText.backToLogin" />
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
      {!config?.headerAuth ? (
        <div className="relative hidden bg-muted lg:block overflow-hidden">
          <img
            src={
              config?.ui?.login_page_image ||
              apiClient.buildEndpoint('/favicon')
            }
            alt="Image"
            className={`absolute inset-0 h-full w-full object-cover ${
              isDarkMode
                ? config?.ui?.login_page_image_dark_filter ||
                  'brightness-[0.2] grayscale'
                : config?.ui?.login_page_image_filter || ''
            }`}
          />
        </div>
      ) : null}
    </div>
  );
}
